import {Component, effect, inject} from '@angular/core';
import {CommonModule, NgClass} from '@angular/common';
import {ModalSwalService} from '@ui/ModalsModule/services/modal.swal.service';

export interface ModalConfig {
  title?: string;
  message?: string;
  type?: 'success' | 'error' | 'warning' | 'info' | 'question';
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  allowOutsideClick?: boolean;
}

@Component({
  selector: 'app-modal-swal',
  standalone: true,
  imports: [
    CommonModule
  ],
  templateUrl: "modal-swal.html"
})
export class ModalSwalComponent {
  private modalService = inject(ModalSwalService);

  isVisible = this.modalService.isVisible;
  config = this.modalService.config;
  isLoading = this.modalService.isLoading;

  constructor() {
    this.initListenEscape();
  }

  private initListenEscape(): void {
    effect((): any => {
      const shouldListen = this.isVisible() && this.config().closeOnEscape;
      if (shouldListen) {
        const handleEscape = (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            this.modalService.close();
          }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
          document.removeEventListener('keydown', handleEscape);
        };
      }
    });
  }

  confirm = () => this.modalService.confirmAction();

  cancel = () => this.modalService.cancelAction();

  onBackdropClick = () => {
    if (this.config().closeOnBackdrop) {
      this.modalService.close();
    }
  };

  getIcon(): string {
    const icons: Record<string, string> = {
      success: '✓',
      error: '✕',
      warning: '!',
      info: 'ℹ',
      question: '?'
    };
    return icons[this.config().type || 'info'];
  }

  getIconClass(): string {
    const classes: Record<string, string> = {
      success: 'bg-green-100 text-green-600',
      error: 'bg-red-100 text-red-600',
      warning: 'bg-yellow-100 text-yellow-600',
      info: 'bg-blue-100 text-blue-600',
      question: 'bg-indigo-100 text-indigo-600'
    };
    return classes[this.config().type || 'info'];
  }

  getBtnClass(): string {
    const classes: Record<string, string> = {
      success: 'bg-green-500 hover:bg-green-600',
      error: 'bg-red-500 hover:bg-red-600',
      warning: 'bg-yellow-500 hover:bg-yellow-600',
      info: 'bg-blue-500 hover:bg-blue-600',
      question: 'bg-gray-500 hover:bg-gray-600'
    };
    return classes[this.config().type || 'info'];
  }
}
